/**
 * Meta progression — docs/05-content-plan.md §4.
 *
 * Five tracks, each fed by a *different* play behaviour so no playstyle starves:
 *
 *   Ash        any run            -> starting kits, stash slots, rest nodes
 *   Codex      reaction discovery -> material-tagged kits, world modifiers, hints
 *   Lineage    deaths             -> body archetypes: you unlock what killed you
 *   Salvage    new biomes reached -> weapon classes, module families, recipes
 *   Attunement boss kills         -> traversal abilities as run-start options
 *
 * Two gate kinds, deliberately:
 *   - `spend`     — Ash is a currency; you choose what to buy. Scarcity is the point.
 *   - `threshold` — the other four are meters; they unlock on their own, because
 *                   asking a player to "spend" the memory of how they died is
 *                   the wrong verb.
 *
 * Anti-farm is structural rather than rate-limited: Codex/Salvage/Attunement
 * award only on *first* observation of a reaction/biome/boss, and Lineage decays
 * per repeated death to the same enemy. You cannot grind one room.
 *
 * Decoupled by design: weapon classes, module families, kits, biomes and enemies
 * are referenced as plain string ids. No imports from combat/, world/, entity/.
 */

import { ARCHETYPE_IDS, DEFAULT_ARCHETYPE, TRAVERSAL_ORDER } from './archetypes';
import type { TraversalAbilityId } from './archetypes';
import { DEFAULT_KIT, KIT_BY_ID } from './kits';
import { normaliseMapEcho } from './mapEcho';
import type { MapEcho } from './mapEcho';
import { normaliseStash } from './stash';
import type { StashItem } from './stash';

export type TrackId = 'ash' | 'codex' | 'lineage' | 'salvage' | 'attunement';

export const TRACK_IDS: readonly TrackId[] = ['ash', 'codex', 'lineage', 'salvage', 'attunement'];

export interface TrackDef {
  id: TrackId;
  name: string;
  fedBy: string;
  /** currency = spent down by purchases; meter = monotonic lifetime total. */
  kind: 'currency' | 'meter';
  /** Lineage splits its total into per-archetype channels. */
  channelled: boolean;
}

export const TRACKS: Readonly<Record<TrackId, TrackDef>> = {
  ash: { id: 'ash', name: 'Ash', fedBy: 'any run', kind: 'currency', channelled: false },
  codex: {
    id: 'codex',
    name: 'Codex',
    fedBy: 'discovering reactions',
    kind: 'meter',
    channelled: false,
  },
  lineage: { id: 'lineage', name: 'Lineage', fedBy: 'deaths', kind: 'meter', channelled: true },
  salvage: {
    id: 'salvage',
    name: 'Salvage',
    fedBy: 'reaching new biomes',
    kind: 'meter',
    channelled: false,
  },
  attunement: {
    id: 'attunement',
    name: 'Attunement',
    fedBy: 'boss kills',
    kind: 'meter',
    channelled: false,
  },
};

// ----------------------------------------------------------------- unlocks

export type UnlockGrant =
  | { kind: 'archetype'; archetype: string }
  | { kind: 'attunement'; ability: TraversalAbilityId }
  | { kind: 'attunement-slot'; slots: number }
  | { kind: 'kit'; kit: string }
  | { kind: 'weapons'; classes: readonly string[] }
  | { kind: 'modules'; families: readonly string[] }
  | { kind: 'recipes'; recipes: readonly string[] }
  | { kind: 'world-modifier'; modifier: string }
  | { kind: 'stash'; slots: number }
  | { kind: 'rest-node'; node: string }
  | { kind: 'passive'; passive: string };

export interface UnlockDef {
  id: string;
  track: TrackId;
  name: string;
  desc: string;
  gate: 'spend' | 'threshold';
  /** Ash price, or meter threshold. */
  cost: number;
  /** Lineage only: which per-archetype channel the threshold reads. */
  channel?: string;
  requires: readonly string[];
  defaultUnlocked?: boolean;
  grants: UnlockGrant;
}

// -- Ash: the currency track. Spendable, so the player authors their own ramp.
const ASH_UNLOCKS: readonly UnlockDef[] = [
  {
    id: 'ash.kit.wanderer',
    track: 'ash',
    name: "Wanderer's Kit",
    desc: 'Sword and pistol. The kit you always have.',
    gate: 'spend',
    cost: 0,
    requires: [],
    defaultUnlocked: true,
    grants: { kind: 'kit', kit: 'kit.wanderer' },
  },
  {
    id: 'ash.stash.1',
    track: 'ash',
    name: 'Stash Slots I',
    desc: '+3 slots that survive death.',
    gate: 'spend',
    cost: 120,
    requires: [],
    grants: { kind: 'stash', slots: 3 },
  },
  {
    id: 'ash.stash.2',
    track: 'ash',
    name: 'Stash Slots II',
    desc: '+3 more stash slots.',
    gate: 'spend',
    cost: 300,
    requires: ['ash.stash.1'],
    grants: { kind: 'stash', slots: 3 },
  },
  {
    id: 'ash.stash.3',
    track: 'ash',
    name: 'Stash Slots III',
    desc: '+4 more stash slots.',
    gate: 'spend',
    cost: 700,
    requires: ['ash.stash.2'],
    grants: { kind: 'stash', slots: 4 },
  },
  {
    id: 'ash.rest.mend',
    track: 'ash',
    name: 'Rest Node: Mending',
    desc: 'Rest nodes restore HP.',
    gate: 'spend',
    cost: 150,
    requires: [],
    grants: { kind: 'rest-node', node: 'rest.mend' },
  },
  {
    id: 'ash.rest.reforge',
    track: 'ash',
    name: 'Rest Node: Reforge',
    // #158: originally described as "re-slot modules", which `loadout.ts`
    // already does for free everywhere (docs/04 §7, deliberately) — this
    // unlock was promising a feature that shipped, unowned by it, under a
    // different name. Repointed at the recipe "reforge" actually names:
    // Splice, the grafting-tier reroll (`meta/crafting.ts`'s `splice-trigger`,
    // gated the normal way by `salvage.craft.grafting`) is now reachable
    // through the ash track too, for a player who has ash and no salvage —
    // `unlockedRecipes()` unions grants, so owning both is a no-op, not a
    // conflict.
    desc: "Rest nodes let you Splice — reroll a weapon's modules.",
    gate: 'spend',
    cost: 400,
    requires: ['ash.rest.mend'],
    grants: { kind: 'recipes', recipes: ['splice-trigger'] },
  },
  {
    id: 'ash.rest.altar',
    track: 'ash',
    name: 'Rest Node: Attunement Altar',
    desc: 'Swap a run-start traversal ability mid-run.',
    gate: 'spend',
    cost: 900,
    requires: ['ash.rest.reforge', 'attune.slots.1'],
    grants: { kind: 'rest-node', node: 'rest.altar' },
  },
  {
    id: 'ash.kit.breaker',
    track: 'ash',
    name: "Breaker's Kit",
    desc: 'Hammer and scattergun. Start loud.',
    gate: 'spend',
    cost: 250,
    requires: [],
    grants: { kind: 'kit', kit: 'kit.breaker' },
  },
  {
    id: 'ash.kit.duelist',
    track: 'ash',
    name: "Duelist's Kit",
    // Was "one free dash charge". `MovementModifiers.dashChargeBonus` is
    // declared and never read by `applyModifiers`, so the charge did not exist
    // to grant; the kit describes what `meta/kits.ts` actually hands over.
    desc: 'Dagger and pistol, with a recoil module that turns every shot into distance.',
    gate: 'spend',
    cost: 250,
    requires: [],
    grants: { kind: 'kit', kit: 'kit.duelist' },
  },
  {
    id: 'ash.kit.alchemist',
    track: 'ash',
    name: "Alchemist's Kit",
    // No flask or reagent-selection system exists, so the promise is the
    // sprayer and the corrosive core it ships with.
    desc: 'An alchemical sprayer and an etching core. Everything is a solvent problem.',
    gate: 'spend',
    cost: 500,
    requires: ['codex.kit.reagent'],
    grants: { kind: 'kit', kit: 'kit.alchemist' },
  },
  {
    id: 'ash.courier',
    track: 'ash',
    name: 'Ash Courier',
    desc: 'Carry 20% of your unbanked ash through death.',
    gate: 'spend',
    cost: 600,
    requires: ['ash.stash.1'],
    grants: { kind: 'passive', passive: 'ash-carry-20' },
  },
  {
    id: 'ash.cartography',
    track: 'ash',
    name: "Cartographer's Echo",
    desc: 'The room graph of a failed run persists into the next one on the same seed.',
    gate: 'spend',
    cost: 450,
    requires: [],
    grants: { kind: 'passive', passive: 'map-echo' },
  },
];

// -- Codex: fed by *discovering* reactions. Rewards curiosity about matter.
//
// The ladder below is priced against a ceiling, not against a feeling. A profile
// can bank `CODEX_PER_DISCOVERY` once per *distinct* reaction event id ever —
// `ReactionEngine` fires `onDiscovery` once per id for the life of the profile —
// so the lifetime maximum is the number of distinct `event` strings in
// `buildReactionRules()`, currently **68** (80 rules, several sharing an id).
//
// The old ladder topped out at 110 and put four unlocks above that ceiling:
// `codex.mod.drowned` 75, `codex.kit.void` 80, `codex.hint.3` 90 and
// `codex.mod.thin-veil` 110 could never fire, for any player, ever. Not a bug
// with a symptom — `evaluate()` is *correct* to never fire them.
//
// The top threshold is now **61**, and 61 is forced rather than chosen. Two
// rules bracket it and they meet at a point:
//
//   - headroom: the ceiling must clear the top by 1.1x, so top <= 68/1.1 = 61.8;
//   - saturation: content past the last unlock is scenery, and the allowance is
//     the same 10% counted in units, ceil(0.1 * 68) = 7, so top >= 68 - 7 = 61.
//
// 61 is the only integer in [61, 61.8]. That squeeze is a property of a track
// that pays 1 per unit and is not fixed by a bigger ceiling — at any ceiling C
// the legal band is [C - ceil(C/10), floor(C/1.1)], which is about 0.009*C wide.
// Authoring more codex events would raise the ladder's resolution, and it should
// happen; it would not buy the ladder any slack, and the rules that carry the
// events live in `sim/reactionData.ts`, which this change does not own.
//
// Everything below is the old ladder scaled by 61/110 and rounded, so the shape
// of the pacing — the gaps, the order, the `requires` chains — is unchanged.
const CODEX_UNLOCKS: readonly UnlockDef[] = [
  {
    id: 'codex.autopsy',
    track: 'codex',
    name: 'Composition Autopsy',
    desc: 'See enemy material composition, and therefore their derived weaknesses.',
    gate: 'threshold',
    cost: 11,
    requires: [],
    grants: { kind: 'passive', passive: 'show-composition' },
  },
  {
    id: 'codex.hint.1',
    track: 'codex',
    name: 'Reaction Hints I',
    desc: 'The codex names the reagent you are missing.',
    gate: 'threshold',
    cost: 6,
    requires: [],
    grants: { kind: 'passive', passive: 'hint-reagent' },
  },
  {
    id: 'codex.hint.2',
    track: 'codex',
    name: 'Reaction Hints II',
    desc: 'The codex names the conditions, not just the pair.',
    gate: 'threshold',
    cost: 22,
    requires: ['codex.hint.1'],
    grants: { kind: 'passive', passive: 'hint-conditions' },
  },
  {
    id: 'codex.hint.3',
    track: 'codex',
    name: 'Reaction Hints III',
    desc: 'The codex predicts chains of length 3.',
    gate: 'threshold',
    cost: 50,
    requires: ['codex.hint.2'],
    grants: { kind: 'passive', passive: 'hint-chains' },
  },
  {
    id: 'codex.kit.reagent',
    track: 'codex',
    name: 'Reagent Kit',
    desc: 'Start with two flasks of any discovered liquid.',
    gate: 'threshold',
    cost: 14,
    requires: [],
    grants: { kind: 'kit', kit: 'kit.reagent' },
  },
  {
    id: 'codex.kit.pyre',
    track: 'codex',
    name: 'Pyre-tagged Kit',
    desc: 'Start with a PYRO-tagged loadout and an accelerant flask.',
    gate: 'threshold',
    cost: 19,
    requires: [],
    grants: { kind: 'kit', kit: 'kit.pyre' },
  },
  {
    id: 'codex.kit.cryo',
    track: 'codex',
    name: 'Cryo-tagged Kit',
    desc: 'Start with a CRYO-tagged loadout. Freeze, then shatter.',
    gate: 'threshold',
    cost: 28,
    requires: [],
    grants: { kind: 'kit', kit: 'kit.cryo' },
  },
  {
    id: 'codex.kit.void',
    track: 'codex',
    name: 'Void-tagged Kit',
    desc: 'Start with a VOID-tagged loadout. The Choir will notice.',
    gate: 'threshold',
    cost: 44,
    requires: ['codex.hint.2'],
    grants: { kind: 'kit', kit: 'kit.void' },
  },
  {
    id: 'codex.mod.volatile',
    track: 'codex',
    name: 'World Modifier: Volatile Strata',
    desc: 'Doubled explosive and accelerant deposits. Everything is a fuse.',
    gate: 'threshold',
    cost: 33,
    requires: [],
    grants: { kind: 'world-modifier', modifier: 'volatile-strata' },
  },
  {
    id: 'codex.mod.drowned',
    track: 'codex',
    name: 'World Modifier: Drowned World',
    desc: 'Liquid volumes scaled up. Fluid Adapt stops being optional.',
    gate: 'threshold',
    cost: 41,
    requires: [],
    grants: { kind: 'world-modifier', modifier: 'drowned-world' },
  },
  {
    id: 'codex.mod.thin-veil',
    track: 'codex',
    name: 'World Modifier: Thin Veil',
    desc: 'Void and temporal materials bleed into every biome.',
    gate: 'threshold',
    cost: 61,
    requires: ['codex.mod.volatile', 'codex.mod.drowned'],
    grants: { kind: 'world-modifier', modifier: 'thin-veil' },
  },
];

// -- Salvage: fed by reaching new biomes. Rewards going *further*.
//
// Ceiling: 12 biomes x `SALVAGE_PER_BIOME` = **120**, banked once per biome.
//
// The old top threshold was 85, which is nine biomes. The last three biomes of a
// twelve-biome game therefore paid into a meter no unlock read — in the one
// track whose entire premise is "rewards going further". Salvage saturating
// before the map does is the same defect as the codex ceiling, wearing the
// opposite sign.
//
// `salvage.craft.grafting` now costs **105**, the eleventh biome. Derived from
// the same two rules that bracket the codex ladder, which for a 12x10 feed are
// wide enough apart to leave a choice:
//
//   - headroom: top <= 120/1.1 = 109.09, so top <= 109;
//   - saturation: the allowance is ceil(0.1 * 120 / 10) = 2 biomes, so the last
//     unlock must land on the 10th biome or later, i.e. top >= 91.
//
// 105 sits inside [91, 109] at the point that reads as a sentence: the final
// crafting tier wants eleven of the twelve biomes, leaving the twelfth as the
// margin the headroom rule requires rather than as dead content.
const SALVAGE_UNLOCKS: readonly UnlockDef[] = [
  {
    id: 'salvage.weapons.blades',
    track: 'salvage',
    name: 'Blade Salvage',
    desc: 'Claw, spear, axe, scythe.',
    gate: 'threshold',
    cost: 10,
    requires: [],
    grants: { kind: 'weapons', classes: ['claw', 'spear', 'axe', 'scythe'] },
  },
  {
    id: 'salvage.weapons.firearms',
    track: 'salvage',
    name: 'Firearm Salvage',
    desc: 'Rifle, scattergun, bow.',
    gate: 'threshold',
    cost: 15,
    requires: [],
    grants: { kind: 'weapons', classes: ['rifle', 'scattergun', 'bow'] },
  },
  {
    id: 'salvage.weapons.heavy',
    track: 'salvage',
    name: 'Heavy Salvage',
    desc: 'Greatblade, hammer, lance, chain weapon, shield.',
    gate: 'threshold',
    cost: 25,
    requires: ['salvage.weapons.blades'],
    grants: {
      kind: 'weapons',
      classes: ['greatblade', 'hammer', 'lance', 'chain-weapon', 'shield'],
    },
  },
  {
    id: 'salvage.weapons.emitters',
    track: 'salvage',
    name: 'Emitter Salvage',
    desc: 'Launcher, rail weapon, shard caster, beam emitter.',
    gate: 'threshold',
    cost: 40,
    requires: ['salvage.weapons.firearms'],
    grants: {
      kind: 'weapons',
      classes: ['launcher', 'rail-weapon', 'shard-caster', 'beam-emitter'],
    },
  },
  {
    id: 'salvage.weapons.exotic',
    track: 'salvage',
    name: 'Exotic Salvage',
    desc: 'Whip, staff, relic blade, drill, living weapon.',
    gate: 'threshold',
    cost: 55,
    requires: ['salvage.weapons.heavy'],
    grants: {
      kind: 'weapons',
      classes: ['whip', 'staff', 'relic-blade', 'drill', 'living-weapon'],
    },
  },
  {
    id: 'salvage.weapons.sprayer',
    track: 'salvage',
    name: 'Alchemical Salvage',
    desc: 'Alchemical sprayer, bio-weapon, drone weapon, thrown rig.',
    gate: 'threshold',
    cost: 70,
    requires: ['salvage.weapons.emitters'],
    grants: {
      kind: 'weapons',
      classes: ['alchemical-sprayer', 'bio-weapon', 'drone-weapon', 'thrown-rig'],
    },
  },
  {
    id: 'salvage.modules.elemental',
    track: 'salvage',
    name: 'Elemental Modules',
    desc: 'Frost and Pyre families.',
    gate: 'threshold',
    cost: 20,
    requires: [],
    grants: { kind: 'modules', families: ['frost', 'pyre'] },
  },
  {
    id: 'salvage.modules.industrial',
    track: 'salvage',
    name: 'Industrial Modules',
    desc: 'Galvanic, Kinetic and Corrosive families.',
    gate: 'threshold',
    cost: 30,
    requires: [],
    grants: { kind: 'modules', families: ['galvanic', 'kinetic', 'corrosive'] },
  },
  {
    id: 'salvage.modules.vital',
    track: 'salvage',
    name: 'Vital Modules',
    desc: 'Blood and Fungal families.',
    gate: 'threshold',
    cost: 35,
    requires: ['salvage.modules.elemental'],
    grants: { kind: 'modules', families: ['blood', 'fungal'] },
  },
  {
    id: 'salvage.modules.arcane',
    track: 'salvage',
    name: 'Arcane Modules',
    desc: 'Void, Temporal and Resonant families.',
    gate: 'threshold',
    cost: 60,
    requires: ['salvage.modules.elemental'],
    grants: { kind: 'modules', families: ['void', 'temporal', 'resonant'] },
  },
  {
    id: 'salvage.modules.synthetic',
    track: 'salvage',
    name: 'Synthetic Modules',
    desc: 'Nanite and Radiant families.',
    gate: 'threshold',
    cost: 65,
    requires: ['salvage.modules.industrial'],
    grants: { kind: 'modules', families: ['nanite', 'radiant'] },
  },
  {
    id: 'salvage.craft.basic',
    track: 'salvage',
    name: 'Field Repair',
    desc: 'Recipes: patch a frame, re-seat a module.',
    gate: 'threshold',
    cost: 12,
    requires: [],
    grants: { kind: 'recipes', recipes: ['repair-frame', 'reseat-module'] },
  },
  {
    id: 'salvage.craft.transmute',
    track: 'salvage',
    name: 'Transmutation',
    desc: 'Recipes: convert a module family, refine reagents.',
    gate: 'threshold',
    cost: 45,
    requires: ['salvage.craft.basic'],
    grants: { kind: 'recipes', recipes: ['transmute-family', 'refine-reagent'] },
  },
  {
    id: 'salvage.craft.grafting',
    track: 'salvage',
    name: 'Grafting',
    desc: 'Recipes: fuse two weapons, splice a trigger module.',
    gate: 'threshold',
    cost: 105,
    requires: ['salvage.craft.transmute', 'salvage.modules.vital'],
    grants: { kind: 'recipes', recipes: ['fuse-weapons', 'splice-trigger'] },
  },
];

// -- Attunement: fed by boss kills. This is the track that changes what *kind*
//    of game a run is: with 0 slots a run is a Metroidvania, with 3 it is a
//    build-expression sprint. Ability order follows the critical path (docs/02
//    §3.1) so attunement never outruns level-design intent.
//
// Ceiling: 12 bosses x `ATTUNEMENT_PER_BOSS` + 12 elites x `ATTUNEMENT_PER_ELITE`
// = 120 + 48 = **168**, each banked once.
//
// The last threshold was 128, which passed the saturation rule by one unit and
// nothing else: cheapest-first that is all twelve elites plus eight of twelve
// bosses — 20 of 24 kill-units, wasting 4 against an allowance of
// ceil(0.1 * 168 / 4) = 5. One elite added or removed anywhere in the roster
// flips it. That is the same defect as Salvage's, one unit short of showing up,
// and it is cheaper to correct now than to correct after a roster edit trips it.
//
// The last threshold is now **140** — every boss in the game (120) plus five
// elites (20) — which is 22 of 24 kill-units cheapest-first, wasting 2 against
// the same allowance of 5, and clears the headroom rule at 168/140 = 1.2x.
const ATTUNEMENT_THRESHOLDS: readonly number[] = [8, 16, 26, 38, 52, 68, 86, 106, 140];

const ATTUNEMENT_UNLOCKS: readonly UnlockDef[] = [
  ...TRAVERSAL_ORDER.map((ability, i): UnlockDef => {
    const prev = i > 0 ? [`attune.${TRAVERSAL_ORDER[i - 1]}`] : [];
    return {
      id: `attune.${ability}`,
      track: 'attunement',
      name: `Attunement: ${ability}`,
      desc: `${ability} becomes selectable at run start.`,
      gate: 'threshold',
      cost: ATTUNEMENT_THRESHOLDS[i],
      requires: prev,
      grants: { kind: 'attunement', ability },
    };
  }),
  {
    id: 'attune.slots.1',
    track: 'attunement',
    name: 'Attunement Slot I',
    desc: 'Begin a run with one attuned traversal ability.',
    gate: 'threshold',
    cost: 20,
    requires: ['attune.airstep'],
    grants: { kind: 'attunement-slot', slots: 1 },
  },
  {
    id: 'attune.slots.2',
    track: 'attunement',
    name: 'Attunement Slot II',
    desc: 'Begin a run with two attuned traversal abilities.',
    gate: 'threshold',
    cost: 60,
    requires: ['attune.slots.1'],
    grants: { kind: 'attunement-slot', slots: 1 },
  },
  {
    id: 'attune.slots.3',
    track: 'attunement',
    name: 'Attunement Slot III',
    desc: 'Begin a run with three attuned traversal abilities.',
    gate: 'threshold',
    cost: 110,
    requires: ['attune.slots.2'],
    grants: { kind: 'attunement-slot', slots: 1 },
  },
];

// -- Lineage: fed by deaths, channelled per archetype. You unlock what killed you.
interface LineageCost {
  archetype: string;
  cost: number;
  requires?: readonly string[];
}

const LINEAGE_COSTS: readonly LineageCost[] = [
  { archetype: 'duelist', cost: 0 },
  { archetype: 'breaker', cost: 80 },
  { archetype: 'scavenger', cost: 80 },
  { archetype: 'hunter', cost: 100 },
  { archetype: 'berserker', cost: 110 },
  { archetype: 'ritualist', cost: 130 },
  { archetype: 'techno-knight', cost: 140 },
  { archetype: 'chrono-monk', cost: 160 },
  { archetype: 'possessed-armor', cost: 150 },
  { archetype: 'bone-construct', cost: 170 },
  { archetype: 'parasite-colony', cost: 180 },
  { archetype: 'semi-liquid-mutant', cost: 190 },
  { archetype: 'segmented-biomech', cost: 210 },
  { archetype: 'spectral-entity', cost: 230 },
  { archetype: 'modular-drone-swarm', cost: 250 },
  { archetype: 'flesh-machine-hybrid', cost: 280 },
  { archetype: 'floating-relic', cost: 320 },
];

const LINEAGE_UNLOCKS: readonly UnlockDef[] = LINEAGE_COSTS.map((c) => ({
  id: `lineage.${c.archetype}`,
  track: 'lineage' as const,
  name: `Lineage: ${c.archetype}`,
  desc: `The ${c.archetype} body becomes selectable at run start.`,
  gate: 'threshold' as const,
  cost: c.cost,
  channel: c.archetype,
  requires: c.requires ?? [],
  defaultUnlocked: c.archetype === DEFAULT_ARCHETYPE,
  grants: { kind: 'archetype' as const, archetype: c.archetype },
}));

export const UNLOCKS: readonly UnlockDef[] = [
  ...ASH_UNLOCKS,
  ...CODEX_UNLOCKS,
  ...LINEAGE_UNLOCKS,
  ...SALVAGE_UNLOCKS,
  ...ATTUNEMENT_UNLOCKS,
];

export const UNLOCK_BY_ID: Readonly<Record<string, UnlockDef>> = Object.fromEntries(
  UNLOCKS.map((u) => [u.id, u]),
);

export function unlocksForTrack(track: TrackId): UnlockDef[] {
  return UNLOCKS.filter((u) => u.track === track);
}

// ------------------------------------------------------- lineage: enemy map

export type EnemyTier = 'base' | 'elite' | 'boss';

export interface LineageSource {
  /** The body you become by dying to this thing. */
  archetype: string;
  tier: EnemyTier;
}

/**
 * enemy id -> archetype. "Dying to a fungal beast advances your progress toward
 * the Parasite Colony body" (docs/05 §4). Every archetype except the default has
 * at least two sources so a player is never blocked by a biome they can't reach.
 *
 * Enemy ids are plain strings owned by entity/ — this map is the contract.
 */
export const LINEAGE_MAP: Readonly<Record<string, LineageSource>> = {
  // Keyed on real ids from `entity/enemyData.ts`. The previous table was an
  // aspirational roster written before the enemies were authored, in a
  // different naming convention: 48 of its 58 keys named creatures that do not
  // exist, so `archetypeForEnemy` returned null and every lineage award
  // resolved to zero. Dying is supposed to be the engine of this game's
  // progression (docs/05 §4, §8) and it advanced nothing at all.
  //
  // The archetype each enemy teaches is chosen from its *composition* — the
  // same principle the damage model already uses (docs/03 §6). You unlock the
  // body that is made of what killed you.
  //
  // `duelist` has no source: it is the body every profile starts with.

  // --- base
  drowned_acolyte: { archetype: 'semi-liquid-mutant', tier: 'base' },
  cistern_warden: { archetype: 'possessed-armor', tier: 'base' },
  // Second Scavenger source, and the reason it is this one. `scavenger` had a
  // single source — `soot_gremlin`, in emberworks/cinder_march — against the
  // rule stated above, so the whole body was gated behind two fire biomes: miss
  // them and the Scavenger cannot be unlocked at all. The Silt Crawler is the
  // roster's closest twin to the gremlin on the two axes this map cares about:
  // composition (CHITIN is the gremlin's second constituent and the crawler's
  // core) and body (SWARM family, halfW 4, ~120 speed, sub-25 HP — precisely the
  // "fast and fragile" the Scavenger archetype is built from). Its biomes,
  // sunken_vault and chlorine_fen, are disjoint from the gremlin's, which is the
  // entire point of the two-source rule.
  //
  // It came from `segmented-biomech`, which keeps two sources (coolant_serpent,
  // the_stillcore) and reads truer for them anyway: a serpent and a reactor core
  // are segmented machines, and a palm-sized chitinous scuttler is scavenging.
  silt_crawler: { archetype: 'scavenger', tier: 'base' },
  furnace_hound: { archetype: 'hunter', tier: 'base' },
  bellows_engine: { archetype: 'techno-knight', tier: 'base' },
  soot_gremlin: { archetype: 'scavenger', tier: 'base' },
  fungal_beast: { archetype: 'parasite-colony', tier: 'base' },
  sporecaller: { archetype: 'parasite-colony', tier: 'base' },
  bark_lurcher: { archetype: 'parasite-colony', tier: 'base' },
  molten_miner: { archetype: 'breaker', tier: 'base' },
  cinder_wretch: { archetype: 'berserker', tier: 'base' },
  bone_titan: { archetype: 'bone-construct', tier: 'base' },
  salt_censer: { archetype: 'ritualist', tier: 'base' },
  plague_alchemist: { archetype: 'ritualist', tier: 'base' },
  fen_leech: { archetype: 'semi-liquid-mutant', tier: 'base' },
  chlorine_bloater: { archetype: 'semi-liquid-mutant', tier: 'base' },
  armored_knight: { archetype: 'possessed-armor', tier: 'base' },
  relic_mech: { archetype: 'techno-knight', tier: 'base' },
  mercury_wisp: { archetype: 'floating-relic', tier: 'base' },
  coolant_serpent: { archetype: 'segmented-biomech', tier: 'base' },
  frostbound_sentry: { archetype: 'possessed-armor', tier: 'base' },
  drone_swarm: { archetype: 'modular-drone-swarm', tier: 'base' },
  biotech_abomination: { archetype: 'flesh-machine-hybrid', tier: 'base' },
  lattice_shaper: { archetype: 'modular-drone-swarm', tier: 'base' },
  void_pilgrim: { archetype: 'spectral-entity', tier: 'base' },
  spectral_revenant: { archetype: 'spectral-entity', tier: 'base' },
  chronomason: { archetype: 'chrono-monk', tier: 'base' },
  echo_thrall: { archetype: 'chrono-monk', tier: 'base' },
  resin_weaver: { archetype: 'flesh-machine-hybrid', tier: 'base' },
  gore_hound: { archetype: 'berserker', tier: 'base' },

  // --- elites
  tidecaller_revenant: { archetype: 'semi-liquid-mutant', tier: 'elite' },
  foreman_of_the_ninth_flue: { archetype: 'breaker', tier: 'elite' },
  mycelial_shepherd: { archetype: 'parasite-colony', tier: 'elite' },
  ashfall_harbinger: { archetype: 'hunter', tier: 'elite' },
  saltbound_inquisitor: { archetype: 'possessed-armor', tier: 'elite' },
  grafted_surgeon: { archetype: 'ritualist', tier: 'elite' },
  verdigris_castellan: { archetype: 'floating-relic', tier: 'elite' },
  cryo_lancer: { archetype: 'techno-knight', tier: 'elite' },
  assembler_prime: { archetype: 'modular-drone-swarm', tier: 'elite' },
  hollow_cantor: { archetype: 'spectral-entity', tier: 'elite' },
  the_late_arrival: { archetype: 'chrono-monk', tier: 'elite' },
  the_hungry_wall: { archetype: 'flesh-machine-hybrid', tier: 'elite' },

  // --- bosses
  the_drowned_choirmaster: { archetype: 'semi-liquid-mutant', tier: 'boss' },
  the_great_bellows: { archetype: 'breaker', tier: 'boss' },
  the_rotwood_mother: { archetype: 'parasite-colony', tier: 'boss' },
  the_kiln_saint: { archetype: 'hunter', tier: 'boss' },
  the_ossuary_cantor: { archetype: 'bone-construct', tier: 'boss' },
  matron_of_the_green_lung: { archetype: 'ritualist', tier: 'boss' },
  the_clockwork_reliquary: { archetype: 'techno-knight', tier: 'boss' },
  the_stillcore: { archetype: 'segmented-biomech', tier: 'boss' },
  the_grey_tide: { archetype: 'modular-drone-swarm', tier: 'boss' },
  the_unlit_choir: { archetype: 'spectral-entity', tier: 'boss' },
  the_hour_that_repeats: { archetype: 'chrono-monk', tier: 'boss' },
  the_marrow_throne: { archetype: 'berserker', tier: 'boss' },
};

export const LINEAGE_ENEMY_IDS: readonly string[] = Object.keys(LINEAGE_MAP);

/**
 * Canonical id form.
 *
 * This table was written in kebab-case and the enemy registry in snake_case,
 * so for the whole life of the project `LINEAGE_MAP['fungal_beast']` was
 * `undefined` and every lineage award silently resolved to zero — the one
 * mechanic docs/05 §8 names as the headline difference from Noita, dead on a
 * separator. Both spellings are the same name, so canonicalise rather than
 * pick a winner; `tests/ids.test.ts` asserts every key resolves to a real
 * enemy, which is what makes this a canonicalisation and not a papering-over.
 */
export function canonId(id: string): string {
  return id.toLowerCase().replace(/-/g, '_');
}

const LINEAGE_BY_CANON: Readonly<Record<string, LineageSource>> = (() => {
  const out: Record<string, LineageSource> = {};
  for (const k of Object.keys(LINEAGE_MAP)) out[canonId(k)] = LINEAGE_MAP[k];
  return out;
})();

/**
 * Award curve. Base 24 with a 0.86 falloff per prior death to the *same* enemy,
 * floored at 5 — so a first death to a new thing is worth ~5 repeat deaths to a
 * familiar one. This makes Lineage reward "die to new things", which is exactly
 * the exploration behaviour the track should encourage, without ever punishing a
 * player for dying to what the biome actually contains.
 */
export const LINEAGE_BASE = 24;
export const LINEAGE_FALLOFF = 0.86;
export const LINEAGE_FLOOR = 5;

const TIER_MULTIPLIER: Readonly<Record<EnemyTier, number>> = {
  base: 1,
  elite: 1.75,
  boss: 3,
};

export function lineageAward(enemyId: string, priorDeathsToThisEnemy: number): number {
  const src = LINEAGE_BY_CANON[canonId(enemyId)];
  if (!src) return 0;
  const decayed = LINEAGE_BASE * Math.pow(LINEAGE_FALLOFF, Math.max(0, priorDeathsToThisEnemy));
  return Math.max(LINEAGE_FLOOR, Math.round(decayed * TIER_MULTIPLIER[src.tier]));
}

/** Which body an enemy is a lineage source for. Null if it isn't one. */
export function archetypeForEnemy(enemyId: string): string | null {
  return LINEAGE_BY_CANON[canonId(enemyId)]?.archetype ?? null;
}

// -------------------------------------------------------------- award feeds

/** Ash yield for finishing a run, by outcome. Extraction pays, dying still pays. */
export const ASH_ON_EXTRACT = 1.0;
export const ASH_ON_DEATH = 0.55;

/** Codex yield per first-time reaction observation. */
export const CODEX_PER_DISCOVERY = 1;
/** Salvage yield per first-time biome arrival. */
export const SALVAGE_PER_BIOME = 10;
/** Attunement yield per first-time boss kill, and per first-time elite kill. */
export const ATTUNEMENT_PER_BOSS = 10;
export const ATTUNEMENT_PER_ELITE = 4;

// ------------------------------------------------------------------ state

export interface TrackState {
  /** Lifetime total awarded. Never decreases. */
  earned: number;
  /** Currency tracks only. */
  spent: number;
}

export interface ProgressionState {
  tracks: Record<TrackId, TrackState>;
  /** archetype id -> lineage progress. The channelled part of the Lineage track. */
  lineage: Record<string, number>;
  /** enemy id -> deaths, drives the lineage falloff curve. */
  deathsBy: Record<string, number>;
  codexSeen: string[];
  biomesSeen: string[];
  bossesKilled: string[];
  elitesKilled: string[];
  unlocked: string[];
  /** Abilities chosen to start the next run with. Validated against slots. */
  startAbilities: string[];
  /** Kit chosen for the next run. Validated against `unlockedKits()`. */
  startKit: string;
  /**
   * World modifiers opted into for the next run (issue #7). Player-chosen and
   * shown on the setup screen — a modifier that appears unannounced is a bug
   * report, so this is stored intent rather than anything the generator decides.
   */
  worldModifiers: string[];
  /**
   * Gear that survives death (issue #5). Sized by `stashSlots()`, which is why
   * it lives here rather than on `RunState`: the run object is discarded by
   * `endRunAndRestart` and "+3 slots that survive death" is the entire promise.
   */
  stash: StashItem[];
  /**
   * The Movement Lab's bench capacity, overriding the earned one.
   *
   * Optional and absent everywhere else. `Game` sets it only for the lab scene,
   * where the stash is stocked with the whole weapon catalogue so two weapons
   * can be compared against the same ground for the price of a Tab. It is not
   * additive: the shelf should be exactly as long as the catalogue, or the
   * panel's free-slot arithmetic stops describing what it is showing.
   *
   * Safe to keep on `ProgressionState` rather than somewhere lab-only because
   * the lab is constructed with a null-storage `SaveSystem` and never writes;
   * `reload()` re-reads the profile from disk.
   */
  labStashSlots?: number;
  /**
   * Unbanked ash carried out of a death by the Ash Courier passive, waiting for
   * the next run to pick it up. Zero without the passive, and cleared by
   * `consumeCarriedAsh` at run creation so it can never be collected twice.
   */
  carriedAsh: number;
  /** The last failed run's room graph, for the `map-echo` passive. Seed-keyed. */
  mapEcho: MapEcho | null;
  lifetime: {
    runs: number;
    deaths: number;
    kills: number;
    deepestBiomeCount: number;
    playtimeMs: number;
  };
}

export interface AwardContext {
  enemyId?: string;
  /** Explicit lineage channel, when there is no enemy (e.g. an environmental death). */
  archetype?: string;
  eventId?: string;
  biomeId?: string;
  bossId?: string;
  reason?: string;
}

export interface UnlockEvent {
  id: string;
  def: UnlockDef;
  track: TrackId;
  /** Human-readable reason, for the toast the UI drains. */
  reason: string;
}

export interface UnlockView {
  def: UnlockDef;
  unlocked: boolean;
  /** Prereqs met and cost payable right now. */
  available: boolean;
  progress: number;
  required: number;
  missingPrereqs: string[];
}

export function createProgressionState(): ProgressionState {
  const tracks = {} as Record<TrackId, TrackState>;
  for (const t of TRACK_IDS) tracks[t] = { earned: 0, spent: 0 };
  const lineage: Record<string, number> = {};
  for (const a of ARCHETYPE_IDS) lineage[a] = 0;
  return {
    tracks,
    lineage,
    deathsBy: {},
    codexSeen: [],
    biomesSeen: [],
    bossesKilled: [],
    elitesKilled: [],
    unlocked: UNLOCKS.filter((u) => u.defaultUnlocked).map((u) => u.id),
    startAbilities: [],
    startKit: DEFAULT_KIT,
    worldModifiers: [],
    stash: [],
    carriedAsh: 0,
    mapEcho: null,
    lifetime: { runs: 0, deaths: 0, kills: 0, deepestBiomeCount: 0, playtimeMs: 0 },
  };
}

// ------------------------------------------------------------- progression

export class Progression {
  readonly state: ProgressionState;
  private pending: UnlockEvent[] = [];
  private unlockedSet: Set<string>;

  constructor(state: ProgressionState = createProgressionState()) {
    // Normalise *in place*. Callers do `new Progression(profile.progression)`
    // and then save `profile` — if we swapped in a fresh object here, every
    // subsequent award would land somewhere the save never sees.
    this.state = Object.assign(state, normaliseState(state));
    this.unlockedSet = new Set(this.state.unlocked);
  }

  // -- core -----------------------------------------------------------------

  /**
   * Feed a track. Threshold tracks may unlock immediately; Ash only banks.
   * Returns whatever unlocked as a result (also queued for `pendingUnlocks`).
   */
  award(track: TrackId, amount: number, context: AwardContext = {}): UnlockEvent[] {
    if (!(track in TRACKS)) throw new Error(`unknown track: ${track}`);
    if (!(amount > 0)) return [];

    if (track === 'lineage') {
      const channel = context.archetype ?? (context.enemyId ? archetypeForEnemy(context.enemyId) : null);
      // A lineage award with no body to advance is meaningless, not an error —
      // an environmental death simply teaches you nothing about a body.
      if (!channel) return [];
      this.state.lineage[channel] = (this.state.lineage[channel] ?? 0) + amount;
    }

    this.state.tracks[track].earned += amount;
    return this.evaluate(context.reason ?? TRACKS[track].fedBy);
  }

  /** Ash only. Returns false if unaffordable, prereq-blocked, or already owned. */
  purchase(id: string): boolean {
    const def = UNLOCK_BY_ID[id];
    if (!def || def.gate !== 'spend') return false;
    if (this.unlockedSet.has(id)) return false;
    if (!this.prereqsMet(def)) return false;
    if (this.balance(def.track) < def.cost) return false;
    this.state.tracks[def.track].spent += def.cost;
    this.grant(def, 'purchased');
    // A purchase can satisfy a prereq that a threshold unlock was waiting on.
    this.evaluate('prerequisite met');
    return true;
  }

  isUnlocked(id: string): boolean {
    return this.unlockedSet.has(id);
  }

  /** Every unlock in the game with its current status — the UI's whole model. */
  unlockables(): UnlockView[] {
    return UNLOCKS.map((def) => {
      const unlocked = this.unlockedSet.has(def.id);
      const progress = this.progressToward(def);
      const missing = def.requires.filter((r) => !this.unlockedSet.has(r));
      return {
        def,
        unlocked,
        available: !unlocked && missing.length === 0 && progress >= def.cost,
        progress,
        required: def.cost,
        missingPrereqs: missing,
      };
    });
  }

  /** Drain the toast queue. The UI is the only caller. */
  pendingUnlocks(): UnlockEvent[] {
    const out = this.pending;
    this.pending = [];
    return out;
  }

  peekPending(): readonly UnlockEvent[] {
    return this.pending;
  }

  // -- track reads ----------------------------------------------------------

  /** Spendable balance. Meters report their lifetime total. */
  balance(track: TrackId): number {
    const t = this.state.tracks[track];
    return TRACKS[track].kind === 'currency' ? t.earned - t.spent : t.earned;
  }

  earned(track: TrackId): number {
    return this.state.tracks[track].earned;
  }

  lineageProgress(archetype: string): number {
    return this.state.lineage[archetype] ?? 0;
  }

  // -- behaviour feeds ------------------------------------------------------
  //
  // These are the intended entry points: they encode *which behaviour feeds
  // which track*, and the first-time-only rules that keep the tracks unfarmable.

  /** Lineage. Death is legible progress toward becoming what killed you. */
  recordDeath(enemyId: string | null): { archetype: string | null; amount: number; events: UnlockEvent[] } {
    this.state.lifetime.deaths += 1;
    if (!enemyId) return { archetype: null, amount: 0, events: [] };
    const prior = this.state.deathsBy[enemyId] ?? 0;
    const amount = lineageAward(enemyId, prior);
    this.state.deathsBy[enemyId] = prior + 1;
    const archetype = archetypeForEnemy(enemyId);
    if (!archetype || amount <= 0) return { archetype, amount: 0, events: [] };
    const events = this.award('lineage', amount, {
      enemyId,
      archetype,
      reason: `killed by ${enemyId}`,
    });
    return { archetype, amount, events };
  }

  /** Codex. First observation only — the codex is a record, not a counter. */
  recordDiscovery(eventId: string): UnlockEvent[] {
    if (this.state.codexSeen.includes(eventId)) return [];
    this.state.codexSeen.push(eventId);
    return this.award('codex', CODEX_PER_DISCOVERY, { eventId, reason: `discovered ${eventId}` });
  }

  /** Salvage. First arrival only — revisiting a biome salvages nothing new. */
  recordBiomeReached(biomeId: string): UnlockEvent[] {
    if (this.state.biomesSeen.includes(biomeId)) return [];
    this.state.biomesSeen.push(biomeId);
    this.state.lifetime.deepestBiomeCount = Math.max(
      this.state.lifetime.deepestBiomeCount,
      this.state.biomesSeen.length,
    );
    return this.award('salvage', SALVAGE_PER_BIOME, { biomeId, reason: `reached ${biomeId}` });
  }

  /** Attunement. First kill only, bosses worth 2.5× an elite. */
  recordBossKill(bossId: string): UnlockEvent[] {
    if (this.state.bossesKilled.includes(bossId)) return [];
    this.state.bossesKilled.push(bossId);
    return this.award('attunement', ATTUNEMENT_PER_BOSS, { bossId, reason: `slew ${bossId}` });
  }

  recordEliteKill(eliteId: string): UnlockEvent[] {
    if (this.state.elitesKilled.includes(eliteId)) return [];
    this.state.elitesKilled.push(eliteId);
    return this.award('attunement', ATTUNEMENT_PER_ELITE, { bossId: eliteId, reason: `slew ${eliteId}` });
  }

  /** Ash. Every run pays; extraction pays roughly twice as well as death. */
  recordRunEnd(rawAsh: number, outcome: 'death' | 'extract' | 'abandon'): UnlockEvent[] {
    this.state.lifetime.runs += 1;
    const mul = outcome === 'extract' ? ASH_ON_EXTRACT : outcome === 'death' ? ASH_ON_DEATH : 0;
    const amount = Math.floor(rawAsh * mul);
    return this.award('ash', amount, { reason: `run ${outcome}` });
  }

  // -- attunement -----------------------------------------------------------
  //
  // The explicit model of "an early run is a Metroidvania, a late run is a
  // build-expression sprint": slots start at 0, so run one grants nothing and
  // every ability must be found in-world. Each slot converts one previously
  // earned ability into a starting condition.

  /** Abilities the player has attuned and may therefore start a run with. */
  attunedAbilities(): TraversalAbilityId[] {
    const out: TraversalAbilityId[] = [];
    for (const id of this.unlockedSet) {
      const g = UNLOCK_BY_ID[id]?.grants;
      if (g && g.kind === 'attunement') out.push(g.ability);
    }
    // Stable critical-path order, so UI listings never reshuffle.
    return TRAVERSAL_ORDER.filter((a) => out.includes(a));
  }

  attunementSlots(): number {
    let n = 0;
    for (const id of this.unlockedSet) {
      const g = UNLOCK_BY_ID[id]?.grants;
      if (g && g.kind === 'attunement-slot') n += g.slots;
    }
    return n;
  }

  runStartOptions(): TraversalAbilityId[] {
    return this.attunementSlots() > 0 ? this.attunedAbilities() : [];
  }

  /** Validate and store the next run's starting abilities. */
  setStartAbilities(ids: readonly string[]): { ok: boolean; reason?: string } {
    const slots = this.attunementSlots();
    if (ids.length > slots) return { ok: false, reason: `only ${slots} attunement slot(s)` };
    const attuned = this.attunedAbilities() as string[];
    for (const id of ids) {
      if (!attuned.includes(id)) return { ok: false, reason: `${id} is not attuned` };
    }
    this.state.startAbilities = [...new Set(ids)];
    return { ok: true };
  }

  /** What a run actually begins with, after slot changes may have invalidated a pick. */
  effectiveStartAbilities(): TraversalAbilityId[] {
    const attuned = this.attunedAbilities();
    return this.state.startAbilities
      .filter((a): a is TraversalAbilityId => attuned.includes(a as TraversalAbilityId))
      .slice(0, this.attunementSlots());
  }

  // -- kits -----------------------------------------------------------------

  /** Validate and store the kit the next run begins with. */
  setStartKit(id: string): { ok: boolean; reason?: string } {
    if (!KIT_BY_ID[id]) return { ok: false, reason: `no such kit: ${id}` };
    if (!this.unlockedKits().includes(id)) return { ok: false, reason: `${id} is not unlocked` };
    this.state.startKit = id;
    return { ok: true };
  }

  /**
   * The kit a run actually begins with. Falls back to the default for the same
   * reason `effectiveStartAbilities` filters: a stored pick can be invalidated
   * between runs, and a run start is past the point where refusing helps.
   */
  effectiveStartKit(): string {
    const id = this.state.startKit;
    return KIT_BY_ID[id] && this.unlockedKits().includes(id) ? id : DEFAULT_KIT;
  }

  // -- world modifiers ------------------------------------------------------
  //
  // Opt-in and visible before BEGIN RUN (issue #7). The same validate-then-store
  // / degrade-on-read pair as kits and abilities, and for the same reason: a
  // stored pick can be invalidated between runs by nothing the player did.

  /** Validate and store the modifiers the next run is generated with. */
  setWorldModifiers(ids: readonly string[]): { ok: boolean; reason?: string } {
    const owned = new Set(this.unlockedWorldModifiers());
    for (const id of ids) {
      if (!owned.has(id)) return { ok: false, reason: `${id} is not unlocked` };
    }
    this.state.worldModifiers = [...new Set(ids)];
    return { ok: true };
  }

  /**
   * What a run is actually generated with. Filters rather than throws: by the
   * time a world is being made, refusing helps nobody, and a modifier silently
   * dropped costs the player that modifier and not the run.
   */
  effectiveWorldModifiers(): string[] {
    const owned = new Set(this.unlockedWorldModifiers());
    return this.state.worldModifiers.filter((id) => owned.has(id));
  }

  // -- carried ash ----------------------------------------------------------

  carriedAsh(): number {
    return this.state.carriedAsh;
  }

  setCarriedAsh(amount: number): void {
    this.state.carriedAsh = Math.max(0, Math.floor(amount));
  }

  /**
   * Take the carried ash and clear it in the same operation.
   *
   * Read-then-clear as two calls is how a carry becomes a duplicator: any path
   * that reads without clearing (a run that fails to start, a setup screen that
   * previews the number) hands the same ash to the next run as well.
   */
  consumeCarriedAsh(): number {
    const n = this.state.carriedAsh;
    this.state.carriedAsh = 0;
    return n;
  }

  // -- map echo -------------------------------------------------------------

  mapEcho(): MapEcho | null {
    return this.state.mapEcho;
  }

  setMapEcho(echo: MapEcho | null): void {
    this.state.mapEcho = echo;
  }

  // -- grant rollups --------------------------------------------------------

  unlockedArchetypes(): string[] {
    return this.grantsOfKind('archetype').map((g) => (g as { archetype: string }).archetype);
  }

  unlockedKits(): string[] {
    return this.grantsOfKind('kit').map((g) => (g as { kit: string }).kit);
  }

  unlockedWeaponClasses(): string[] {
    const out = new Set<string>();
    for (const g of this.grantsOfKind('weapons')) {
      for (const c of (g as { classes: readonly string[] }).classes) out.add(c);
    }
    return [...out];
  }

  unlockedModuleFamilies(): string[] {
    const out = new Set<string>();
    for (const g of this.grantsOfKind('modules')) {
      for (const f of (g as { families: readonly string[] }).families) out.add(f);
    }
    return [...out];
  }

  unlockedRecipes(): string[] {
    const out = new Set<string>();
    for (const g of this.grantsOfKind('recipes')) {
      for (const r of (g as { recipes: readonly string[] }).recipes) out.add(r);
    }
    return [...out];
  }

  unlockedWorldModifiers(): string[] {
    return this.grantsOfKind('world-modifier').map((g) => (g as { modifier: string }).modifier);
  }

  unlockedRestNodes(): string[] {
    return this.grantsOfKind('rest-node').map((g) => (g as { node: string }).node);
  }

  unlockedPassives(): string[] {
    return this.grantsOfKind('passive').map((g) => (g as { passive: string }).passive);
  }

  stashSlots(): number {
    // The lab's bench overrides the earned count outright rather than adding
    // to it, so the shelf is exactly as long as the catalogue and the panel's
    // free-slot arithmetic still describes what it is showing. Only `Game` sets
    // this, only in the lab scene, and the lab never writes to disk.
    const lab = this.state.labStashSlots;
    if (typeof lab === 'number' && lab > 0) return lab;
    let n = 0;
    for (const g of this.grantsOfKind('stash')) n += (g as { slots: number }).slots;
    return n;
  }

  // -- internals ------------------------------------------------------------

  private grantsOfKind(kind: UnlockGrant['kind']): UnlockGrant[] {
    const out: UnlockGrant[] = [];
    for (const id of this.unlockedSet) {
      const g = UNLOCK_BY_ID[id]?.grants;
      if (g && g.kind === kind) out.push(g);
    }
    return out;
  }

  private prereqsMet(def: UnlockDef): boolean {
    for (const r of def.requires) if (!this.unlockedSet.has(r)) return false;
    return true;
  }

  private progressToward(def: UnlockDef): number {
    if (def.track === 'lineage' && def.channel) return this.lineageProgress(def.channel);
    return this.balance(def.track);
  }

  /**
   * Sweep threshold unlocks until nothing more fires. Looping matters: unlocking
   * A can satisfy B's prerequisite in the same award, and the player should see
   * both toasts rather than one now and one after the next kill.
   */
  private evaluate(reason: string): UnlockEvent[] {
    const fired: UnlockEvent[] = [];
    for (let pass = 0; pass < UNLOCKS.length; pass++) {
      let any = false;
      for (const def of UNLOCKS) {
        if (def.gate !== 'threshold') continue;
        if (this.unlockedSet.has(def.id)) continue;
        if (!this.prereqsMet(def)) continue;
        if (this.progressToward(def) < def.cost) continue;
        fired.push(this.grant(def, reason));
        any = true;
      }
      if (!any) break;
    }
    return fired;
  }

  private grant(def: UnlockDef, reason: string): UnlockEvent {
    this.unlockedSet.add(def.id);
    this.state.unlocked.push(def.id);
    const ev: UnlockEvent = { id: def.id, def, track: def.track, reason };
    this.pending.push(ev);
    return ev;
  }
}

/**
 * Repair a state object loaded from disk: fill tracks/channels added since the
 * save was written, drop unlock ids that no longer exist, dedupe. Save-schema
 * migration handles shape; this handles *content* drift, which happens far more
 * often than shape drift and must never throw.
 */
export function normaliseState(s: ProgressionState): ProgressionState {
  const fresh = createProgressionState();
  const tracks = {} as Record<TrackId, TrackState>;
  for (const t of TRACK_IDS) {
    const src = s.tracks?.[t];
    tracks[t] = { earned: src?.earned ?? 0, spent: src?.spent ?? 0 };
  }
  const lineage: Record<string, number> = {};
  for (const a of ARCHETYPE_IDS) lineage[a] = s.lineage?.[a] ?? 0;

  const known = new Set(UNLOCKS.map((u) => u.id));
  const unlocked = [...new Set([...(s.unlocked ?? []), ...fresh.unlocked])].filter((id) =>
    known.has(id),
  );

  return {
    tracks,
    lineage,
    deathsBy: { ...(s.deathsBy ?? {}) },
    codexSeen: [...new Set(s.codexSeen ?? [])],
    biomesSeen: [...new Set(s.biomesSeen ?? [])],
    bossesKilled: [...new Set(s.bossesKilled ?? [])],
    elitesKilled: [...new Set(s.elitesKilled ?? [])],
    unlocked,
    startAbilities: [...new Set(s.startAbilities ?? [])],
    // Content drift, not shape drift: a kit id that no longer exists must fall
    // back to the default rather than start a run with empty hands.
    startKit: typeof s.startKit === 'string' && s.startKit ? s.startKit : DEFAULT_KIT,
    // A modifier id that no longer exists is dropped here rather than at the
    // generator, so a stale profile cannot ask worldgen for a world shape that
    // has no definition. `resolveWorldModifiers` skips unknowns too — this is
    // the belt, that is the braces.
    worldModifiers: [...new Set(s.worldModifiers ?? [])],
    stash: normaliseStash(s.stash),
    carriedAsh: Math.max(0, Math.floor(Number(s.carriedAsh) || 0)),
    mapEcho: normaliseMapEcho(s.mapEcho),
    lifetime: { ...fresh.lifetime, ...(s.lifetime ?? {}) },
  };
}

// `progressionSummary` stood here: headline numbers for a meta screen, with no
// caller in `src/`, no internal use and no test — the twelfth instance this
// sweep found of fully authored state with no consumer (#117). Deleted rather
// than wired, for `EnemyDef.ash`'s reason: the meta screen reads `earned`,
// `balance` and `isUnlocked` off `Progression` directly, so this was a second
// spelling of numbers that already had one. `tests/liveness.test.ts` is what
// keeps it deleted.
